import { Article, ArticleResolution, ArticleType } from '@wix/answers-api';
import { Box } from '@wix/design-system';

import { ArticleFeedbackBar } from './ArticleFeedbackBar';
import { PinnedChatInput } from './PinnedChatInput';
import { Step } from '../../../pages/article/[articleTitle]';
import { ArticleWithComponents } from '../../../types';
import { useStore } from '../../../zustand/stores';
import { CopilotDisplayMode } from '../../../zustand/stores/copilot';
import { ArticleContent } from '../../ArticleContent';
import { BreadcrumbItem } from '../../Breadcrumbs';

import css from './index.module.scss';

const noop = () => undefined;

type ArticleMainContentProps = {
  article: ArticleWithComponents;
  relatedArticles: Article[];
  steps: Step[];
  breadcrumbItems: BreadcrumbItem[];
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
  onClickStatus?: (resolution: ArticleResolution) => Promise<void>;
  onExpandRightPanel: () => void;
  isChatbotEpHidden?: boolean;
};

export const ArticleMainContent = ({
  article,
  relatedArticles,
  steps,
  breadcrumbItems,
  copilotArticleContentRef,
  isLoading,
  selfArticleRedirect,
  onClickStatus,
  onExpandRightPanel,
  isChatbotEpHidden = false,
}: ArticleMainContentProps) => {
  const { user } = useStore((state) => state.userSlice);
  const { copilotDisplayMode } = useStore((state) => state.copilotSlice);
  const isLoggedIn = user !== null;
  const isChatbotActive =
    copilotDisplayMode === CopilotDisplayMode.Chatbot ||
    copilotDisplayMode === CopilotDisplayMode.Loading;
  const relatedQuestions = article.relatedQuestions as string[] | undefined;

  return (
    <Box className={css.wrapper} ref={copilotArticleContentRef} direction="vertical">
      <Box className={css.contentSection} direction="vertical">
        <ArticleContent
          article={article}
          readingTimeInMinutes={article.readingTimeInMinutes}
          onClickStatus={onClickStatus}
          relatedArticles={relatedArticles}
          steps={steps}
          breadcrumbItems={breadcrumbItems}
          showLeftSideBar={true}
          setShowLeftSideBar={noop}
          copilotArticleContentRef={copilotArticleContentRef}
          showRightSideBar={true}
          setShowRightSideBar={noop}
          isLoading={isLoading}
          selfArticleRedirect={selfArticleRedirect}
        />
      </Box>

      {!isLoading && article.type === ArticleType.ARTICLE && (
        <ArticleFeedbackBar
          articleId={article.id}
          onExpandRightPanel={onExpandRightPanel}
          scrollContainerRef={copilotArticleContentRef}
          isChatbotEpHidden={isChatbotEpHidden}
        />
      )}

      {isLoggedIn && !isChatbotEpHidden && (
        <PinnedChatInput
          article={article}
          relatedQuestions={relatedQuestions}
          hidden={isChatbotActive || isLoading}
        />
      )}
    </Box>
  );
};
